import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService } from '../services/auth.service';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    email: string | null;
    role: string | null;
    login: (email: string, token: string) => void;
    logout: () => void;
    checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [email, setEmail] = useState<string | null>(null);
    const [role, setRole] = useState<string | null>(null);

    const checkAuth = useCallback(async (): Promise<boolean> => {
        if (!authService.isAuthenticated()) {
            setIsAuthenticated(false);
            setEmail(null);
            setRole(null);
            return false;
        }

        try {
            const result = await authService.verifySession();
            setIsAuthenticated(result.valid);
            setEmail(result.valid ? authService.getEmail() : null);
            setRole(result.valid && result.user ? result.user.role : null);
            return result.valid;
        } catch {
            setIsAuthenticated(false);
            setEmail(null);
            setRole(null);
            return false;
        }
    }, []);

    const login = useCallback((email: string, token: string) => {
        authService.saveAuth(email, token);
        setIsAuthenticated(true);
        setEmail(email);
    }, []);

    const logout = useCallback(() => {
        authService.logout();
        setIsAuthenticated(false);
        setEmail(null);
        setRole(null);
    }, []);

    // Check authentication on mount
    useEffect(() => {
        const initAuth = async () => {
            setIsLoading(true);
            await checkAuth();
            setIsLoading(false);
        };
        initAuth();
    }, [checkAuth]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                isLoading,
                email,
                role,
                login,
                logout,
                checkAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
